import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from '@mdi/js';
import './ClassesHelpModal.css';

const ClassTermsHelpModal = ({ isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState([]);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!isOpen) setExpandedSections([]);
  }, [isOpen]);

  const toggleSection = (sectionId) => {
    const isExpanding = !expandedSections.includes(sectionId);
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    if (isExpanding) {
      setTimeout(() => {
        sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  };

  if (!isOpen) return null;

  const sections = [
    {
      id: 'setting-up-new-term',
      title: '1. Setting Up a New Term',
      content: (
        <>
          <p>Click <strong>"New Term"</strong> to open the 3-step wizard. It guides you through everything in one go — no need to find separate buttons afterwards.</p>
          <p><strong>Step 1 of 3: Term Details.</strong> Enter a name, start date, and end date (or pick a duration and the end date fills automatically). You can also open enrolment immediately. Click Next when ready.</p>
          <p><strong>Step 2 of 3: Copy Classes.</strong> Select a previous term to copy its class schedule into the new term. A checklist lets you pick which classes to carry across. Deselect any you don't want. If you'd rather start from scratch, click <strong>"Start Fresh"</strong> — the term is created immediately with no classes.</p>
          <p><strong>Step 3 of 3: Roll Over Students.</strong> Review the classes being copied. The "Also roll over confirmed students" checkbox is ticked by default. Leave it on to carry confirmed enrolments across as Unconfirmed. Untick it if you want to start fresh with no students. Click <strong>"Create Term"</strong> to finish.</p>
          <p>After the wizard closes, you can add more classes manually using the + button on the term card.</p>
          <p><strong>Opening and closing enrolment:</strong> The padlock icon on each term row controls whether enrolment is open. A closed padlock means enrolment is closed. Click it to open enrolment (you'll be asked to confirm). An open padlock means enrolment is open. Click it to close enrolment again. Closing enrolment hides that term from your public class pages and shared links; it does not affect existing enrolments, payments, or student data, and you can re-open it at any time.</p>
        </>
      )
    },
    {
      id: 'enrolment-statuses',
      title: '2. Enrolment Statuses',
      content: (
        <>
          <p>Every student enrolled in a class has one of three statuses:</p>
          <ul>
            <li><strong>Confirmed:</strong> enrolled and paid (or payment-exempt). Shows green on the check-in screen.</li>
            <li><strong>Unconfirmed:</strong> carried over from the previous term via rollover but not yet re-enrolled or paid for the new term. Shows red/amber on check-in as a prompt to follow up.</li>
            <li><strong>Pending:</strong> a new student who submitted an enquiry from your public page and is awaiting placement by an admin.</li>
          </ul>
          <p>You can change a student's status manually from the Students page if needed.</p>
        </>
      )
    },
    {
      id: 'copy-classes',
      title: '3. Copying Classes Between Terms',
      content: (
        <>
          <p><strong>What gets copied:</strong> Class name, dance style, level, day of week, start and end time, room name, primary instructors, max student cap, and approval requirement. Enrolments and attendance history are <strong>not</strong> copied. Those belong to the original term.</p>
          <p><strong>How to copy:</strong> Class copying happens in Step 2 of the "New Term" wizard. Select a source term and pick the classes you want. You can deselect individual classes before confirming.</p>
          <p><strong>After copying:</strong> Each new class is linked back to the original via an internal link. This is what makes student rollover work — the system uses it to find the matching class in the new term when copying enrolments.</p>
          <p><strong>Can I edit after copying?</strong> Yes. Copied classes are independent. Change the time, room, or instructors freely without affecting the original term's data.</p>
          <p><strong>Adding classes later:</strong> Use the + button on the term card to add individual classes manually after the term has been created.</p>
        </>
      )
    },
    {
      id: 'rolling-over-students',
      title: '4. Rolling Over Students',
      content: (
        <>
          <p><strong>What rollover does:</strong> Takes every student with a <strong>Confirmed</strong> enrolment in the source term and creates a new <strong>Unconfirmed</strong> enrolment for the matching class in the new term.</p>
          <p><strong>How to roll over:</strong> Student rollover happens in Step 3 of the "New Term" wizard, immediately after you copy classes. The "Also roll over confirmed students" checkbox is ticked by default. Untick it if you want to start the new term with no students carried across.</p>
          <p><strong>Matching logic:</strong> The system matches students to classes using the internal link created during the class copy step. Classes you added manually (without copying) will not have this link, so rollover will not find matches for them.</p>
          <p><strong>Duplicates:</strong> If a student is already enrolled in the target class (e.g., they re-enrolled themselves), rollover skips them. No duplicate enrolments are created.</p>
          <p><strong>Attendance history:</strong> Not carried over. Each term starts fresh.</p>
          <p><strong>Automatic emails:</strong> When rollover runs, each student automatically receives a confirmation email with a link to acknowledge their spot in the new term. These are sent in the background after the term is created.</p>
          <p><strong>Re-enrolment notification:</strong> In Step 3 of the wizard, tick "Send re-enrolment notification email" to also send a custom notification asking students to re-enrol. You can edit the subject and body before sending. This is separate from the automatic confirmation and is the best way to prompt students who haven't acted yet.</p>
          <p><strong>After rollover:</strong> Unconfirmed students appear on the check-in screen with a red/amber indicator. If a student says they did not receive an email, use the Send Emails portal to contact them directly.</p>
        </>
      )
    },
    {
      id: 'short-courses',
      title: '5. Short Courses',
      content: (
        <>
          <p><strong>What are short courses?</strong> Standalone intensive workshops or mini-series, separate from your weekly term classes. Examples: "Head Motion Intensive", "Musicality Workshop", "Beginner Crash Course".</p>
          <p><strong>Creating a short course:</strong> Click "New Short Course" (below the terms section). Set a name, style, level, start date (the day of week is derived from this), number of sessions, and start/end time.</p>
          <p><strong>Re-running a past course:</strong> Once a short course's start date has passed, it moves into a "Past Courses" section. Click <strong>Re-run</strong> on any past course to open a pre-filled New Short Course modal with all the original details. Just pick a new start date and save. The original course stays in Past Courses for reference.</p>
          <p><strong>Enrolments:</strong> Students enrol into short courses the same way as term classes: via your public group page, directly from the Check-In screen as a walk-in, or manually via the Students page. When enrolling, you can optionally assign a leader or follower role for that enrolment. This is set per enrolment. The same student can have different roles across different classes or short courses.</p>
        </>
      )
    },
    {
      id: 'history-tab',
      title: '6. History Tab',
      content: (
        <>
          <p>The Terms &amp; Courses page has two tabs:</p>
          <ul>
            <li><strong>Terms &amp; Courses</strong> (default) — shows only current and upcoming terms, active short courses, and groups. This is your working view for day-to-day management.</li>
            <li><strong>History</strong> — a read-only list of past terms and past short courses. Useful for checking what ran in previous seasons, copying dates or names, or looking up past class schedules. Items in History are greyed out and labelled "Ended".</li>
          </ul>
          <p>Action buttons (New Term, New Short Course, New Group) always appear above the tabs so they're accessible from either view.</p>
        </>
      )
    },
    {
      id: 'deleting-terms-classes',
      title: '7. Deleting Terms & Classes',
      content: (
        <>
          <p><strong>Deleting a class:</strong> Only possible if the class has no active enrolments. If students are enrolled, remove them first (or use class cancellation, which handles refunds if payments exist).</p>
          <p><strong>Deleting a term:</strong> Only possible if all classes within it have no active enrolments. Old terms are safe to keep. They don't affect active data and remain available for rollover as a source.</p>
          <p><strong>Cancelling a class:</strong> Use the cancel flow (accessible from the class edit button) to properly close a class, notify students, and issue refunds where applicable. This marks the class inactive without deleting its history.</p>
        </>
      )
    }
  ];

  return (
    <div className="classes-help-overlay" onClick={onClose}>
      <div className="classes-help-modal" onClick={e => e.stopPropagation()}>
        <div className="classes-help-header">
          <div className="classes-help-header-content">
            <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
            <h2>Terms & Classes Help</h2>
          </div>
          <button className="classes-help-close" onClick={onClose}>×</button>
        </div>

        <div className="classes-help-body">
          {sections.map(section => (
            <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
              <button
                className="help-accordion-header"
                onClick={() => toggleSection(section.id)}
              >
                <Icon
                  path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                  size={0.8}
                  color="var(--dl-color-purple-default)"
                />
                <span>{section.title}</span>
              </button>
              {expandedSections.includes(section.id) && (
                <div className="help-accordion-content">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="classes-help-footer">
          <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
        </div>
      </div>
    </div>
  );
};

export default ClassTermsHelpModal;
